import { useEffect, useMemo, useState } from "react";
import Modal from "@modalSrc/Modal";
import {
  formatContentVersionChangedAt,
  type ContentLanguageVersion,
} from "@/utils/contentVersionHistory";

export type ContentVersionNoteUpdate = {
  id: number;
  changeNote: string;
};

type ContentVersionNoteModalProps = {
  isOpen: boolean;
  versions: ContentLanguageVersion[];
  isSaving?: boolean;
  onClose: () => void;
  onSave: (updates: ContentVersionNoteUpdate[]) => void | Promise<void>;
  zIndex?: number;
};

function formatVersionActor(version: ContentLanguageVersion): string {
  return (version.changedByName || "System").trim() || "System";
}

export default function ContentVersionNoteModal({
  isOpen,
  versions,
  isSaving = false,
  onClose,
  onSave,
  zIndex = 9200,
}: ContentVersionNoteModalProps) {
  const [notesById, setNotesById] = useState<Record<number, string>>({});
  const [editingNoteIds, setEditingNoteIds] = useState<Record<number, true>>({});

  useEffect(() => {
    if (!isOpen) return;

    const initialNotes: Record<number, string> = {};
    for (const version of versions) {
      initialNotes[version.id] = version.changeNote ?? "";
    }
    setNotesById(initialNotes);
    setEditingNoteIds({});
  }, [isOpen, versions]);

  const pendingUpdates = useMemo(() => (
    versions
      .filter((version) => (notesById[version.id] ?? "") !== (version.changeNote ?? ""))
      .map((version) => ({
        id: version.id,
        changeNote: notesById[version.id] ?? "",
      }))
  ), [notesById, versions]);

  function beginEditingNote(versionId: number) {
    setEditingNoteIds((current) => ({
      ...current,
      [versionId]: true,
    }));
  }

  if (!isOpen || versions.length === 0) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Version details"
      size="xl"
      className="contentVersionNoteModal"
      zIndex={zIndex}
      footer={(
        <>
          <div className="footerLeft" />
          <div className="footerCenter" />
          <div className="footerRight">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void onSave(pendingUpdates)}
              disabled={isSaving || pendingUpdates.length === 0}
            >
              {isSaving ? "Saving..." : "Save notes"}
            </button>
          </div>
        </>
      )}
    >
      <div className="contentVersionNotesTableWrap">
        <table className="contentVersionNotesTable">
          <thead>
            <tr>
              <th scope="col">Changed</th>
              <th scope="col">By</th>
              <th scope="col">Notes</th>
            </tr>
          </thead>
          <tbody>
            {versions.map((version, index) => {
              const actor = formatVersionActor(version);
              const changedLabel = formatContentVersionChangedAt(version.changedAt);
              const isCurrent = index === 0;
              const noteText = notesById[version.id] ?? "";
              const isEditingNote = !!editingNoteIds[version.id];
              const noteAriaLabel = `Notes for version changed ${changedLabel} by ${actor}`;

              return (
                <tr
                  key={version.id}
                  className={[
                    isEditingNote ? undefined : "contentVersionNotesRowCompact",
                    isCurrent ? "contentVersionNotesRowCurrent" : "contentVersionNotesRowPrevious",
                  ].filter(Boolean).join(" ")}
                >
                  <td className="contentVersionNotesChangedCell">
                    {changedLabel}
                  </td>
                  <td className="contentVersionNotesByCell">{actor}</td>
                  <td className="contentVersionNotesFieldCell">
                    {isEditingNote ? (
                      <textarea
                        id={`contentVersionChangeNote-${version.id}`}
                        className="contentVersionNoteTextarea"
                        rows={3}
                        maxLength={2000}
                        value={noteText}
                        disabled={isSaving}
                        autoFocus
                        aria-label={noteAriaLabel}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setNotesById((current) => ({
                            ...current,
                            [version.id]: nextValue,
                          }));
                        }}
                        onBlur={(event) => {
                          if (event.currentTarget.value.trim()) return;
                          setEditingNoteIds((current) => {
                            if (!current[version.id]) return current;
                            const next = { ...current };
                            delete next[version.id];
                            return next;
                          });
                        }}
                        placeholder="Add context for this version (optional)."
                      />
                    ) : noteText.trim() ? (
                      <button
                        type="button"
                        className="contentVersionNoteDisplay"
                        disabled={isSaving}
                        aria-label={noteAriaLabel}
                        onClick={() => beginEditingNote(version.id)}
                      >
                        {noteText}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="contentVersionNoteDisplay contentVersionNoteDisplayEmpty"
                        disabled={isSaving}
                        aria-label={`${noteAriaLabel}. Click to add a note.`}
                        onClick={() => beginEditingNote(version.id)}
                      >
                        Add note
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
